import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Loader2, ArrowLeft, Building2, AlertTriangle } from "lucide-react";
import { supabase, getTenantHeaders } from "@/integrations/supabase/tenantClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";
import { extractDomainFromEmail } from "@/hooks/useSSOConfigurations";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const emailSchema = z.string()
  .min(1, "Email is required")
  .email("Please enter a valid email address")
  .max(255, "Email must be less than 255 characters");

interface SSOLoginFormProps {
  onBack: () => void;
  onSSORedirect: (redirectUrl: string) => void;
  parRedirectUri?: string | null;
}

// System subdomains that are not tenant-specific
const SYSTEM_SUBDOMAINS = ['login', 'platform', 'auth', 'www', 'api', 'app'];

/**
 * Extract target subdomain from a redirect URL
 */
function extractTargetSubdomain(redirectUrl: string | null): string | null {
  if (!redirectUrl) return null;
  
  try {
    const url = new URL(redirectUrl);
    const hostname = url.hostname.toLowerCase();
    
    // Check if it's a cnvrgnx.com subdomain
    if (hostname.endsWith('.cnvrgnx.com')) {
      const subdomain = hostname.replace('.cnvrgnx.com', '');
      // Exclude system subdomains
      if (!SYSTEM_SUBDOMAINS.includes(subdomain)) {
        return subdomain;
      }
    }
    return null;
  } catch {
    return null;
  }
}

interface TenantValidationError {
  message: string;
  targetOrgName?: string;
  allowedDomains?: string[];
}

export function SSOLoginForm({ onBack, onSSORedirect, parRedirectUri }: SSOLoginFormProps) {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [tenantError, setTenantError] = useState<TenantValidationError | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setTenantError(null);
    setOrganizationName(null);

    // Validate email
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      setEmailError(emailResult.error.errors[0].message);
      return;
    }

    const domain = extractDomainFromEmail(email);
    if (!domain) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      // Get redirect URL - prioritize PAR redirect URI, then search params, then fallback
      const redirectParam = searchParams.get('redirect');
      const redirectUri = parRedirectUri || redirectParam || `${window.location.origin}/flowgrid`;
      const targetSubdomain = extractTargetSubdomain(parRedirectUri || redirectParam);
      
      console.log('[SSOLoginForm] Using redirect URI:', redirectUri);

      // Pre-validate tenant access if we have a target subdomain
      if (targetSubdomain) {
        console.log('[SSOLoginForm] Pre-validating email for tenant:', targetSubdomain);
        
        const { data: validation, error: validationError } = await supabase.functions.invoke("validate-tenant-email", {
          body: { email, target_subdomain: targetSubdomain },
          headers: getTenantHeaders(),
        });

        if (validationError) {
          console.error('[SSOLoginForm] Tenant validation error:', validationError);
          // Continue with SSO flow on error - the backend will handle it
        } else if (validation && !validation.is_valid) {
          // Email domain not authorized for this tenant
          console.log('[SSOLoginForm] Tenant validation failed:', validation);
          setTenantError({
            message: validation.error_message || `Your email domain is not authorized for this workspace.`,
            targetOrgName: validation.target_org_name,
            allowedDomains: validation.allowed_domains
          });
          setLoading(false);
          return;
        }
      }

      // Call edge function to check if SSO is enabled for this domain
      const { data, error } = await supabase.functions.invoke("check-sso-domain", {
        body: { email, redirect_uri: redirectUri },
        headers: getTenantHeaders(),
      });

      if (error) {
        toast({
          title: "Error checking SSO",
          description: "Unable to verify SSO configuration. Please try again.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (!data?.sso_enabled) {
        setEmailError("SSO is not configured for this email domain. Please use password login.");
        setLoading(false);
        return;
      }

      if (data.organization_name) {
        setOrganizationName(data.organization_name);
      }

      // Redirect to SSO provider
      if (data.redirect_url) {
        onSSORedirect(data.redirect_url);
      } else {
        toast({
          title: "SSO Configuration Error",
          description: "SSO redirect URL not configured. Please contact your administrator.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("SSO check error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Logo and title */}
      <div className="flex flex-col items-start space-y-6">
        <Logo size="lg" />
        <div className="text-left space-y-2">
          <h1 className="text-2xl font-realhead tracking-tight text-white">Sign in with SSO</h1>
          <p className="text-gray-400">Enter your work email to continue with your organization's identity provider</p>
        </div>
      </div>

      {/* Tenant access denied error */}
      {tenantError && (
        <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{tenantError.message}</p>
            {tenantError.allowedDomains && tenantError.allowedDomains.length > 0 && (
              <p className="text-sm opacity-80">
                Authorized domains: {tenantError.allowedDomains.join(', ')}
              </p>
            )}
            <p className="text-sm opacity-80">
              If you believe this is an error, please contact your IT administrator.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="sso-email" className="text-gray-300">Work Email</Label>
          <Input
            id="sso-email"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              // Clear tenant error when user changes email
              if (tenantError) setTenantError(null);
            }}
            disabled={loading}
            autoComplete="email"
            autoFocus
          />
          {emailError && (
            <p className="text-sm text-destructive">{emailError}</p>
          )}
        </div>

        {organizationName && organizationName !== 'Default Organization' && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-4 w-4" />
            <span className="text-sm">Signing in to {organizationName}</span>
          </div>
        )}

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Checking SSO...
            </>
          ) : (
            "Continue with SSO"
          )}
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to password login
        </button>
      </form>
    </div>
  );
}
